import { useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import ConfirmationPopover from "@/appComponents/Blocks/Composites/ConfirmationPopover/ConfirmationPopover";
import { appstr } from "@coreapp/utils/appstr";
import { Text } from "@theorgchartdev/storybook";

export const metaDataTypes = { 0: "Remote", 1: "Local" };

export const enumAutoAssignmentFullAccess = {
  readOnly: "_FA_RO",
  readWrite: "_FA_RW",
  admin: "_FA_ADM"
};

export const autoAssignmentDisplayStrings = {
  [enumAutoAssignmentFullAccess.readOnly]: "fullaccess0",
  [enumAutoAssignmentFullAccess.readWrite]: "fullaccess1",
  [enumAutoAssignmentFullAccess.admin]: "fullaccess2"
};

export const BuildUserList = (users, clickHandler) => {
  let userOptions = [];

  for (const userIdx in users) {
    let userObj = users[userIdx];

    const userContent = {
      content: (
        <ActionListItem
          padding="large"
          label={userObj["username"] ? userObj["username"] : userObj["email"]}
          actions={[
            {
              iconName: "gear",
              onClick: () => clickHandler(userObj),
              dataTestId: `account-settings-authorization-user-${userIdx}-edit-btn`
            }
          ]}
        />
      ),
      padding: false,
      divider: true,
      value: userIdx
    };
    userOptions.push(userContent);
  }
  return userOptions;
};

const SSOListItem = ({ ssoIdx, entityID, clickHandler, warnOnDelete }) => {
  const [keepActive, setKeepActive] = useState(false);

  return (
    <ActionListItem
      padding="large"
      visible={keepActive ? "always" : "hover"}
      label={`${appstr("SSO Entity ID")}: ${entityID}`}
      actions={[
        {
          iconName: "gear",
          onClick: () => clickHandler("edit", ssoIdx),
          dataTestId: `account-settings-authorization-sso-${ssoIdx}-edit-btn`
        },
        {
          content: (
            <ConfirmationPopover
              onSubmit={() => clickHandler("delete", ssoIdx)}
              icon={"trash"}
              color="error"
              iconOnly
              otherHeaderProps={{ title: appstr("Delete SSO Configuration") }}
              confirmLabel={appstr("Delete")}
              triggerTestId={`account-settings-authorization-sso-${ssoIdx}-delete-btn`}
              dataTestPrefix={`account-settings-authorization-sso-${ssoIdx}-delete`}
              popoverProps={{
                onOpen: () => setKeepActive(true),
                onClose: () => setKeepActive(false)
              }}
            >
              <Text>
                {appstr(warnOnDelete ? "SSO Save Warning" : "Confirm delete SSO configuration")}
              </Text>
            </ConfirmationPopover>
          )
        }
      ]}
    />
  );
};

export const BuildSSOList = (ssoMapping, clickHandler) => {
  let ssoOptions = [];
  const mappings = Object.values(ssoMapping);

  for (const ssoIdx in ssoMapping) {
    let ssoObj = ssoMapping[ssoIdx];
    let entityID = ssoObj.entityID ? ssoObj.entityID : "<Not Set>";
    const warnOnDelete = isIncompleteSSOSave(
      mappings,
      mappings.filter(m => m !== ssoObj)
    );

    ssoOptions.push({
      content: (
        <SSOListItem
          ssoIdx={ssoIdx}
          entityID={entityID}
          clickHandler={clickHandler}
          warnOnDelete={warnOnDelete}
        />
      ),
      padding: false,
      divider: true,
      value: ssoIdx
    });
  }
  return ssoOptions;
};

export const removeSSOMappingAt = (ssoMapping, ssoIdx) =>
  ssoMapping.filter((_, idx) => idx !== Number(ssoIdx));

export const isIncompleteSSOSave = (before, after) => {
  if (after.length === 0) return true;
  return before.some(m => m.enabled) && !after.some(m => m.enabled);
};

export const BuildWhitelistOptions = (user, clickHandler) => {
  let whitelistOptions = [];

  if (user?.domainWhitelist.length > 0) {
    const domains = user.domainWhitelist;
    for (const idx in domains) {
      const domainContent = {
        content: (
          <ActionListItem
            label={domains[idx]}
            actions={[
              {
                iconName: "pen",
                onClick: () => clickHandler("edit", idx),
                dataTestId: `account-settings-authorization-whitelist-${idx}-edit-btn`
              },
              {
                iconName: "trash",
                onClick: () => clickHandler("delete", idx),
                dataTestId: `account-settings-authorization-whitelist-${idx}-delete-btn`
              }
            ]}
          />
        ),
        divider: true,
        value: idx
      };
      whitelistOptions.push(domainContent);
    }
  }

  return whitelistOptions;
};

export const BuildLocalMetadataOptions = (localMetadata, clickHandler, dataTestIdPrefix) => {
  let metadataOptions = [];

  for (const idx in localMetadata) {
    const metadataContent = {
      label: localMetadata[idx],
      value: localMetadata[idx],
      clickHandler: e => clickHandler(e.value),
      ...(dataTestIdPrefix && { dataTestId: `${dataTestIdPrefix}-${localMetadata[idx]}-option` })
    };

    metadataOptions.push(metadataContent);
  }

  return metadataOptions;
};

export const rolesOptions = ["Read-Only Role", "Read / Write Role", "Admin Role"];

export const BuildAccessGroupOptions = (
  groups,
  clickHandler,
  mapIdx = null,
  addFullAccess,
  dataTestIdPrefix
) => {
  let groupOptions = [];

  for (var i in groups) {
    const groupContent = {
      label: groups[i].name,
      value: i,
      clickHandler: mapIdx
        ? e => clickHandler(mapIdx, e.value)
        : e => clickHandler("_default", e.value),
      ...(dataTestIdPrefix && { dataTestId: `${dataTestIdPrefix}-group-${i}-option` })
    };

    groupOptions.push(groupContent);
  }

  if (addFullAccess) {
    for (const idx in rolesOptions) {
      const roleContent = {
        label: appstr("fullaccess" + idx.toString()),
        value: rolesOptions[idx],
        clickHandler: e => clickHandler(mapIdx, e.value, idx),
        ...(dataTestIdPrefix && { dataTestId: `${dataTestIdPrefix}-role-${idx}-option` })
      };

      groupOptions.push(roleContent);
    }
  }

  return groupOptions;
};

export const MetadataTypeOptions = clickHandler => {
  return [
    {
      label: "Remote",
      value: 0,
      clickHandler: e => clickHandler(e.value),
      dataTestId: "account-settings-authorization-sso-metadata-type-remote-option"
    },
    {
      label: "Local",
      value: 1,
      clickHandler: e => clickHandler(e.value),
      dataTestId: "account-settings-authorization-sso-metadata-type-local-option"
    }
  ];
};

export const NameIDTypeOptions = clickHandler => {
  return [
    {
      label: appstr("main SAML assertion"),
      value: false,
      clickHandler: e => clickHandler(e.value),
      dataTestId: "account-settings-authorization-sso-nameid-main-assertion-option"
    },
    {
      label: appstr("SAML attributes"),
      value: true,
      clickHandler: e => clickHandler(e.value),
      dataTestId: "account-settings-authorization-sso-nameid-saml-attributes-option"
    }
  ];
};

export const commonSelectProps = {
  selectSize: "medium",
  optionSize: "medium",
  position: "bottom"
};

export const GetMapValue = mapValue => {
  if (mapValue instanceof Object) return rolesOptions[mapValue["role"]];
  else return mapValue;
};

//AutoAssignment

export const BuildChartSelectOptions = (charts, key, clickHandler, dataTestIdPrefix) => {
  let options = [];

  for (const chartID in charts) {
    const chartContent = {
      label: charts[chartID].name,
      value: charts[chartID].ID,
      clickHandler: e => clickHandler(key, e.value),
      ...(dataTestIdPrefix && { dataTestId: `${dataTestIdPrefix}-${charts[chartID].ID}-option` })
    };

    options.push(chartContent);
  }

  return options;
};

export const BuildFieldSelectOptions = (fields, key, clickHandler, dataTestIdPrefix) => {
  const options = fields.map(field => {
    return {
      label: field,
      value: field,
      clickHandler: e => clickHandler(key, e.value),
      ...(dataTestIdPrefix && { dataTestId: `${dataTestIdPrefix}-${field}-option` })
    };
  });

  return options;
};

export const BuildUAGroupOptions = (mapping, accessGroups, clickHandler) => {
  const options = [];
  const fullaccessOptions = Object.values(enumAutoAssignmentFullAccess);

  for (const i in mapping) {
    if (i !== "_default") {
      const groupAssignment = mapping[i];
      let label = accessGroups[groupAssignment]?.name;

      if (fullaccessOptions.includes(groupAssignment)) {
        label = appstr(autoAssignmentDisplayStrings[groupAssignment]);
      }

      const mapContent = {
        content: (
          <ActionListItem
            dataTestId={`auth-group-mapping-${i}`}
            label={`${i}: ${label || appstr("fullaccess")}`}
            actions={[
              {
                iconName: "trash",
                onClick: () => clickHandler(i),
                dataTestId: `auth-group-mapping-${i}-delete`
              }
            ]}
          />
        ),
        divider: true,
        value: i
      };
      options.push(mapContent);
    }
  }

  return options;
};
